import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './InventoryMgmtConfig.css';

export default function InventoryMgmtConfig() {
  const navigate = useNavigate();

  const [excluded,  setExcluded]  = useState([]);   // [{sku, title}]
  const [allSkus,   setAllSkus]   = useState([]);   // [{sku, title}] — not yet excluded
  const [query,     setQuery]     = useState('');
  const [open,      setOpen]      = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();
  const dropRef  = useRef();

  const [trackedCategories,   setTrackedCategories]   = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryQuery,       setCategoryQuery]       = useState('');
  const [categoryOpen,        setCategoryOpen]        = useState(false);
  const [excludeBundles,      setExcludeBundles]      = useState(false);
  const catDropRef = useRef();

  function reload() {
    setLoading(true);
    axios.get('/api/inventory-mgmt/config/not-tracked')
      .then(r => {
        setExcluded(r.data.excluded);
        setAllSkus(r.data.all_skus);
        setTrackedCategories(r.data.tracked_categories || []);
        setAvailableCategories(r.data.available_categories || []);
        setExcludeBundles(!!r.data.exclude_bundles);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load config'); setLoading(false); });
  }

  useEffect(() => { reload(); }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handle(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
      if (catDropRef.current && !catDropRef.current.contains(e.target)) setCategoryOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = allSkus.filter(s => {
    const q = query.toLowerCase();
    return s.sku.toLowerCase().includes(q) || s.title.toLowerCase().includes(q);
  }).slice(0, 40);

  function addSku(sku) {
    setSaving(true);
    axios.post('/api/inventory-mgmt/config/not-tracked', { sku })
      .then(() => { setQuery(''); setOpen(false); reload(); setSaving(false); })
      .catch(() => { setError('Failed to add SKU'); setSaving(false); });
  }

  function removeSku(sku) {
    setSaving(true);
    axios.delete(`/api/inventory-mgmt/config/not-tracked/${encodeURIComponent(sku)}`)
      .then(() => { reload(); setSaving(false); })
      .catch(() => { setError('Failed to remove SKU'); setSaving(false); });
  }

  const filteredCategories = availableCategories.filter(c => {
    const q = categoryQuery.toLowerCase();
    return c.toLowerCase().includes(q) && !trackedCategories.some(tc => tc.toLowerCase() === c.toLowerCase());
  }).slice(0, 40);

  function addCategory(category) {
    category = (category || '').trim();
    if (!category) return;
    setSaving(true);
    axios.post('/api/inventory-mgmt/config/tracked-categories', { category })
      .then(() => { setCategoryQuery(''); setCategoryOpen(false); reload(); setSaving(false); })
      .catch(() => { setError('Failed to add category'); setSaving(false); });
  }

  function removeCategory(category) {
    setSaving(true);
    axios.delete(`/api/inventory-mgmt/config/tracked-categories/${encodeURIComponent(category)}`)
      .then(() => { reload(); setSaving(false); })
      .catch(() => { setError('Failed to remove category'); setSaving(false); });
  }

  function toggleExcludeBundles() {
    const next = !excludeBundles;
    setExcludeBundles(next);
    setSaving(true);
    axios.post('/api/inventory-mgmt/config/bundles', { exclude: next })
      .then(() => setSaving(false))
      .catch(() => { setError('Failed to update bundle setting'); setExcludeBundles(!next); setSaving(false); });
  }

  return (
    <div className="imcfg-page">
      <div className="imcfg-header">
        <button className="imcfg-back" onClick={() => navigate('/inventory-management')}>
          ← Back
        </button>
        <div>
          <h1>Inventory Report Exclusions</h1>
          <p className="imcfg-muted">
            Control what shows up in every Inventory Management report: individually excluded SKUs, which
            product categories are tracked, and whether bundle SKUs are included.
          </p>
        </div>
      </div>

      {error && (
        <div className="imcfg-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <h2 className="imcfg-section-title">
        Excluded SKUs{!loading && ` (${excluded.length})`}
      </h2>

      {/* Add SKU search */}
      <div className="imcfg-add-section">
        <label className="imcfg-add-label">Add SKU to exclusion list</label>
        {allSkus.length === 0 && !loading && (
          <p className="imcfg-muted-sm">
            Run an analysis first so SKU names are available.
          </p>
        )}
        <div className="imcfg-search-wrap" ref={dropRef}>
          <input
            ref={inputRef}
            className="imcfg-search"
            placeholder="Search by SKU or product name…"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            disabled={allSkus.length === 0 || saving}
          />
          {open && filtered.length > 0 && (
            <div className="imcfg-dropdown">
              {filtered.map(s => (
                <button key={s.sku} className="imcfg-drop-item"
                  onMouseDown={e => { e.preventDefault(); addSku(s.sku); }}>
                  <span className="imcfg-drop-sku">{s.sku}</span>
                  <span className="imcfg-drop-title">{s.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Excluded list */}
      {loading ? (
        <p className="imcfg-muted">Loading…</p>
      ) : excluded.length === 0 ? (
        <p className="imcfg-muted">No SKUs excluded yet.</p>
      ) : (
        <div className="imcfg-table-wrap">
          <table className="imcfg-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {excluded.map(row => (
                <tr key={row.sku}>
                  <td className="imcfg-sku">{row.sku}</td>
                  <td className="imcfg-title">{row.title || <span className="imcfg-unknown">—</span>}</td>
                  <td>
                    <button className="imcfg-remove" disabled={saving}
                      onClick={() => removeSku(row.sku)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="imcfg-section-title">
        Tracked Categories{!loading && ` (${trackedCategories.length})`}
      </h2>
      <p className="imcfg-muted-sm">
        Only SKUs whose Shopify product type is in this list appear in reports — everything else
        (apparel, packaging, etc.) is hidden. Remove all categories to track everything.
      </p>

      <div className="imcfg-add-section">
        <label className="imcfg-add-label">Add category to tracked list</label>
        <div className="imcfg-search-wrap" ref={catDropRef}>
          <input
            className="imcfg-search"
            placeholder="Search or type a category name…"
            value={categoryQuery}
            onChange={e => { setCategoryQuery(e.target.value); setCategoryOpen(true); }}
            onFocus={() => setCategoryOpen(true)}
            onKeyDown={e => { if (e.key === 'Enter') addCategory(categoryQuery); }}
            disabled={saving}
          />
          {categoryOpen && categoryQuery && filteredCategories.length > 0 && (
            <div className="imcfg-dropdown">
              {filteredCategories.map(c => (
                <button key={c} className="imcfg-drop-item"
                  onMouseDown={e => { e.preventDefault(); addCategory(c); }}>
                  <span className="imcfg-drop-sku">{c}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <p className="imcfg-muted">Loading…</p>
      ) : trackedCategories.length === 0 ? (
        <p className="imcfg-muted">No categories set — every category is tracked.</p>
      ) : (
        <div className="imcfg-table-wrap">
          <table className="imcfg-table">
            <thead>
              <tr>
                <th>Category</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {trackedCategories.map(category => (
                <tr key={category}>
                  <td className="imcfg-sku">{category}</td>
                  <td>
                    <button className="imcfg-remove" disabled={saving}
                      onClick={() => removeCategory(category)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="imcfg-section-title">Bundle SKUs</h2>
      <label className="imcfg-toggle-row">
        <input
          type="checkbox"
          checked={excludeBundles}
          disabled={loading || saving}
          onChange={toggleExcludeBundles}
        />
        <span>
          Exclude inner/master carton bundle SKUs (<code>MA-</code> / <code>IN-</code> prefix) from reports
        </span>
      </label>
    </div>
  );
}
